<template>
    <div class="bg-gray-100 pt-[5.25rem] lg:pt-24">
        <a id="main-content" class="fixed top-0" tabindex="-1"></a>

        <div class="container">
            <div class="grid grid-cols-12 base-responsive-gap">
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* DETAILS */ }}
                {{ /* * */ }}
                <div class="col-span-12 lg:col-span-4 lg:row-span-2 order-2 lg:order-1">
                    {{/* This differs just slightly from .header-1, making font size smaller */}}
                    {{/* on lg-xl screens to accommodate the word 'programsorozat' in its container */}}
                    <h1 class="font-Poppins font-semibold text-3xl xl:text-4xl 2xl:text-6xl uppercase">
                        {{ model.name }}
                    </h1>

                    <h3 class="lg-lead mt-2 md:mt-4">{{ model.sub_title }}</h3>

                    <div
                        v-if="model.date || model.start_date"
                        class="md-lead border-t-[1px] border-gray-600 pt-4 mt-4 md:pt-8 md:mt-8"
                    >
                        {{ /* $getFormattedEventInterval has logic regarding the text_date_only prop as well. */ }}
                        {{ $getFormattedEventInterval(model).displayed }}

                        <div v-if="!model.text_date_only && model.date" class="mt-2">{{ model.date }}</div>
                    </div>

                    <div v-if="eventLocation" class="mt-4 md:mt-8">
                        <div class="font-Poppins font-semibold text-sm uppercase">
                            <T t="event.location" />
                        </div>
                        <div class="font-Poppins lg:text-lg font-semibold mt-1">{{ eventLocation }}</div>
                        <div v-if="model.place" class="font-Poppins lg:text-lg font-semibold mt-1">
                            {{ model.place }}
                        </div>
                    </div>

                    <div v-if="model.organizer" class="mt-4 md:mt-8">
                        <div class="font-Poppins font-semibold text-sm uppercase">
                            <T t="event.organizer" />
                        </div>
                        <div class="font-Poppins lg:text-lg font-semibold mt-1">
                            {{ model.organizer }}
                        </div>
                    </div>

                    <!-- info -->
                    <div v-if="model.description" class="border-t-[1px] border-gray-600 pt-4 mt-4">
                        <div class="flex items-start">
                            <i class="pi pi-info-circle text-xl"></i>
                            <div class="ml-4" v-html="model.description"></div>
                        </div>
                    </div>

                    <!-- share -->
                    <div class="border-t-[1px] border-gray-600 pt-4 mt-4">
                        <div class="flex items-start">
                            <i class="pi pi-share-alt text-xl"></i>
                            <a
                                :href="`https://www.facebook.com/sharer/sharer.php?u=${
                                    $config.siteBaseURL + $route.fullPath
                                }`"
                                target="_blank"
                                class="uppercase ml-4"
                            >
                                <T t="event.share" />
                            </a>
                        </div>
                    </div>

                    <div class="-mx-2">
                        <Button
                            v-if="model.ticket_url"
                            :to="model.ticket_url"
                            target="_blank"
                            :label="$t('generic.purchase_ticket')"
                            class="mt-8 mx-2"
                        />

                        <Button
                            v-if="model.registration_url"
                            :to="model.registration_url"
                            target="_blank"
                            :label="$t('event.register')"
                            class="mt-8 mx-2"
                        />
                    </div>
                </div>

                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* HERO IMAGE */ }}
                {{ /* * */ }}
                <div v-if="model.media.lead" class="col-span-12 lg:col-span-8 order-1 lg:order-2">
                    <div class="aspect-w-16 aspect-h-9 relative md:mb-8">
                        <picture>
                            <source media="(min-width:992px)" :srcset="$getMediaImage(model.media, 'lead', 'lg')" />
                            <source media="(min-width:480px)" :srcset="$getMediaImage(model.media, 'lead', 'md')" />
                            <img
                                :src="$getMediaImage(model.media, 'lead', 'sm')"
                                :alt="model.name"
                                class="w-full h-full object-cover object-center base-responsive-rounding"
                            />
                        </picture>
                    </div>
                </div>

                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* CONTENT */ }}
                {{ /* * */ }}
                <div
                    v-if="model.components.length"
                    class="col-span-12 lg:col-span-8 mt-12 lg:mt-0 mb-4 lg:mb-8 order-3"
                >
                    <EditableContentComponentGroup :components="model.components" has-parent-container no-overflow />
                </div>

                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* * */ }}
                {{ /* EVENTS */ }}
                {{ /* * */ }}
                <div class="col-span-12 lg:col-span-8 lg:col-start-5 order-4">
                    <div class="grid grid-cols-1 gap-12 lg:gap-24">
                        <div v-if="futureEvents.length" class="col-span-1 grid grid-cols-1 gap-4 lg:gap-7">
                            <ProgramCard
                                v-for="(event, i) in futureEvents"
                                :key="`future-event-${i}`"
                                :title="event.name"
                                :date="$getFormattedEventInterval(event)"
                                :text-date="event.date"
                                :display-text-date-only="event.text_date_only"
                                :description="event.lead_text"
                                :event-types="event.categories_id"
                                :img="$getMediaImage(event.media, 'lead', 'sm')"
                                :location="$getEventLocationName(event.location_id)"
                                :ticket-url="event.ticket_url"
                                :registration-url="event.registration_url"
                                :url="$getEventUrl(event)"
                                :has-series="!!event.series"
                                with-category-icons
                            />
                        </div>

                        <div v-if="pastEvents.length" class="col-span-1">
                            <h2 class="header-2 pb-6"><T t="search.past_events" /></h2>
                            <div class="grid grid-cols-1 gap-4 lg:gap-7 separator-border-top-line">
                                <ProgramCard
                                    v-for="(event, i) in pastEvents"
                                    :key="`past-event-${i}`"
                                    :title="event.name"
                                    :date="$getFormattedEventInterval(event)"
                                    :text-date="event.date"
                                    :display-text-date-only="event.text_date_only"
                                    :description="event.lead_text"
                                    :event-types="event.categories_id"
                                    :img="$getMediaImage(event.media, 'lead', 'sm')"
                                    :location="$getEventLocationName(event.location_id)"
                                    :ticket-url="null"
                                    :registration-url="null"
                                    :url="$getEventUrl(event)"
                                    :has-series="!!event.series"
                                    with-category-icons
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* * */ }}
        {{ /* PROGRAM RECOMMENDATIONS */ }}
        {{ /* * */ }}
        <section v-if="recommendedEvents.length" class="mt-16 sm:mt-20">
            <div class="container grid grid-cols-12 mb-10">
                <div class="col-span-12 sm:col-span-6 lg:col-span-7">
                    <h2 class="header-2"><T t="event.recommended_events" /></h2>
                </div>
            </div>
            <CardCarousel type="program" :items="recommendedEvents" />
        </section>
    </div>
</template>

<script>
import CardCarousel from '~/components/Carousels/CardCarousel/CardCarousel.vue';
import EditableContentComponentGroup from '~/components/Generic/EditableContentComponentGroup.vue';
import ProgramCard from '~/components/Cards/ProgramCard/ProgramCard.vue';

export default {
    name: 'ProgramSeries',
    components: { ProgramCard, EditableContentComponentGroup, CardCarousel },
    async asyncData({ $axios, params, redirect, localePath, store }) {
        let model = null;
        let recommendedEvents = [];

        await $axios
            .$get(`/series/by-slug/${params.programSeries}`)
            .then((res) => {
                model = res;
                recommendedEvents = store.state.data.recommendedEvents.filter((x) => x.id !== model.id).slice(0, 5);
            })
            .catch((err) => {
                console.log(err);
            });

        if (!model) {
            return redirect(localePath('programs-in-varosliget'));
        }

        model.periodic = true;

        return {
            model,
            recommendedEvents,
        };
    },
    head() {
        return {
            title: this.$getPageTitle(this.model.metadata),
            meta: this.$getPageMetaData(this.model.metadata),
        };
    },
    computed: {
        eventLocation() {
            return this.$getEventLocationName(this.model.location_id);
        },
        pastEvents() {
            const ret = this.model.events.filter((event) => this.$dayjs().isAfter(this.$dayjs(event.start_date)));

            ret.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

            return ret;
        },
        futureEvents() {
            return this.model.events.filter((event) => this.$dayjs().isSameOrBefore(this.$dayjs(event.start_date)));
        },
    },
};
</script>

<style scoped></style>
